import {
  appEventProperties,
  getClientContextProperties,
  getOrganisationKey,
  getPersonProperties,
  getResolvedClientContext,
  getUserContextProperties,
  getUserDistinctId,
  resetPostHogContext,
  syncPostHogOrganisationContext,
} from "@/lib/posthog/context"
import { isObjectRecord, isPostHogAuthUser, isPostHogOrganisation } from "@/lib/posthog/guards"
import { getPostHogRoute, getPostHogRouteClientId } from "@/lib/posthog/route"
import {
  consumePendingPostHogAuthState,
  getPostHogClient,
  postHogState,
  setPendingPostHogAuthState,
} from "@/lib/posthog/state"
import type { PostHogAuthUser, PostHogOrganisation } from "@/lib/posthog/types"
import { withoutUndefinedValues } from "@/lib/posthog/utils"

export function syncPostHogIdentityFromAuthState(authState: unknown) {
  if (!isObjectRecord(authState)) {
    return
  }

  if (authState.authenticated === false) {
    if (!getPostHogClient()) {
      setPendingPostHogAuthState(authState)
      return
    }
    resetPostHogIdentity()
    return
  }

  if (authState.authenticated !== true || !isPostHogAuthUser(authState.user)) {
    return
  }

  if (!getPostHogClient()) {
    setPendingPostHogAuthState(authState)
    return
  }

  syncPostHogIdentity({
    user: authState.user,
    activeOrganisation: isPostHogOrganisation(authState.activeOrganisation) ? authState.activeOrganisation : null,
  })
}

export function replayPendingPostHogIdentity() {
  const authState = consumePendingPostHogAuthState()
  if (authState !== null) {
    syncPostHogIdentityFromAuthState(authState)
  }
}

function syncPostHogIdentity({
  activeOrganisation,
  user,
}: {
  activeOrganisation: PostHogOrganisation | null
  user: PostHogAuthUser
}) {
  const client = getPostHogClient()
  if (!client) {
    return
  }

  const distinctId = getUserDistinctId(user.id)
  const personProperties = getPersonProperties(user)
  const personPropertiesSignature = JSON.stringify(personProperties)

  if (postHogState.lastIdentifiedDistinctId !== distinctId) {
    client.identify(distinctId, personProperties)
    postHogState.lastIdentifiedDistinctId = distinctId
    postHogState.lastPersonPropertiesSignature = personPropertiesSignature
  } else if (postHogState.lastPersonPropertiesSignature !== personPropertiesSignature) {
    client.setPersonProperties(personProperties)
    postHogState.lastPersonPropertiesSignature = personPropertiesSignature
  }

  client.register(getUserContextProperties(user))
  syncPostHogOrganisationContext(client, activeOrganisation)
  captureAuthSessionLoaded(user, activeOrganisation)
}

function resetPostHogIdentity() {
  const client = getPostHogClient()
  if (!client) {
    return
  }

  client.reset()
  resetPostHogContext()
}

function captureAuthSessionLoaded(user: PostHogAuthUser, organisation: PostHogOrganisation | null) {
  const client = getPostHogClient()
  if (!client) {
    return
  }

  const route = getPostHogRoute()
  const organisationKey = organisation ? getOrganisationKey(organisation.id) : ""
  const signature = `${getUserDistinctId(user.id)}:${organisationKey}:${route}`
  if (postHogState.lastAuthSessionLoadedSignature === signature) {
    return
  }

  client.capture(
    "auth_session_loaded",
    withoutUndefinedValues({
      ...appEventProperties({
        route,
        ...getClientContextProperties(getResolvedClientContext(getPostHogRouteClientId(window.location.pathname))),
      }),
      user_id: String(user.id),
      organisation_id: organisation?.id,
      organisation_key: organisation ? getOrganisationKey(organisation.id) : undefined,
    }),
  )
  postHogState.lastAuthSessionLoadedSignature = signature
}
